import { redactUrl, resolveUploadUrl } from 'ts-components/PiChat/uploadUrl';

export type Warn = (message: string) => void;

export const defaultWarn: Warn = (message) => {
  // eslint-disable-next-line no-console -- a refused or failed upload has no other trace on the client
  console.warn(message);
};

export type PutStatus = 'stored' | 'expired' | 'failed';

export const presignedPut = async (
  { url, headers }: { url: string; headers: Record<string, string> },
  body: Blob,
  label: string,
  signal: AbortSignal,
  fetchImpl: typeof fetch,
  warn: Warn
): Promise<PutStatus> => {
  try {
    const response = await fetchImpl(url, {
      method: 'PUT',
      headers: { ...headers },
      body,
      signal,
      credentials: 'omit',
      redirect: 'error',
    });
    if (response.ok || response.status === 412) return 'stored';
    warn(
      `${label} upload to ${redactUrl(url)} failed: HTTP ${response.status}`
    );
    return 'failed';
  } catch (error) {
    if (signal.aborted) {
      warn(`${label} upload expired before it finished`);
      return 'expired';
    }
    const name = error instanceof Error ? error.name : 'Error';
    warn(`${label} upload to ${redactUrl(url)} failed: ${name}`);
    return 'failed';
  }
};

export const resolveUploadUrls = (
  urls: string[],
  wsUrl: string,
  label: string,
  warn: Warn
): string[] | null => {
  const resolved = urls.map((url) => resolveUploadUrl(url, wsUrl));
  if (resolved.every((url): url is string => Boolean(url))) return resolved;
  const refused = urls.filter((_, i) => !resolved[i]).map(redactUrl);
  warn(`${label} refused: upload URL not allowed (${refused.join(', ')})`);
  return null;
};

export const presignedPutBeforeExpiry = async <T>(
  expiresAt: number,
  expiredMessage: string,
  warn: Warn,
  run: (signal: AbortSignal) => Promise<T>
): Promise<T | 'expired'> => {
  const remainingMs = expiresAt - Date.now();
  if (remainingMs <= 0) {
    warn(expiredMessage);
    return 'expired';
  }
  const expiry = new AbortController();
  const timer = setTimeout(() => expiry.abort(), remainingMs);
  try {
    return await run(expiry.signal);
  } finally {
    clearTimeout(timer);
  }
};
